import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { StatistiqueService } from 'src/app/core/services/statistique.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastService } from 'src/app/core/services/toast.service';

type Periode = 'MOIS_EN_COURS' | 'MOIS_PRECEDENT' | '7_JOURS' | 'CUSTOM' | 'TOUT';

@Component({
  selector: 'app-dashboard-comptable',
  templateUrl: './dashboard-comptable.component.html',
  styleUrls: ['./dashboard-comptable.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComptableComponent implements OnInit {
  chargement = true;
  data: any = null;
  rechercheCommande = '';
  afficherSeulementEnAttente = true;
  derniereMaj = '';

  // Filtre de période
  periodeSelectionnee: Periode = 'MOIS_EN_COURS';
  dateDebut = '';
  dateFin = '';

  readonly PERIODES: { val: Periode; label: string }[] = [
    { val: 'MOIS_EN_COURS',   label: 'Ce mois' },
    { val: 'MOIS_PRECEDENT',  label: 'Mois précédent' },
    { val: '7_JOURS',         label: '7 derniers jours' },
    { val: 'TOUT',            label: 'Tout l\'historique' },
    { val: 'CUSTOM',          label: 'Personnalisé' },
  ];

  // Onglet table validation
  ongletActif: 'DECLARATION' | 'SANS_DECLARATION' = 'DECLARATION';

  private readonly MODE_META: Record<string, { label: string; icon: string; iconClass: string; barClass: string }> = {
    CASH:         { label: 'Espèces',      icon: 'fa-money-bill-wave', iconClass: 'mode--cash',     barClass: 'bar--cash' },
    MOBILE_MONEY: { label: 'Mobile Money', icon: 'fa-mobile-alt',      iconClass: 'mode--mobile',   barClass: 'bar--mobile' },
    VIREMENT:     { label: 'Virement',     icon: 'fa-university',      iconClass: 'mode--virement', barClass: 'bar--virement' },
    CARTE:        { label: 'Carte',        icon: 'fa-credit-card',     iconClass: 'mode--carte',    barClass: 'bar--carte' },
    AUTRE:        { label: 'Autre',        icon: 'fa-ellipsis-h',      iconClass: 'mode--autre',    barClass: 'bar--autre' },
    ESPECE:       { label: 'Espèces',      icon: 'fa-money-bill-wave', iconClass: 'mode--cash',     barClass: 'bar--cash' },
    CHEQUE:       { label: 'Chèque',       icon: 'fa-file-invoice',    iconClass: 'mode--autre',    barClass: 'bar--autre' },
    TRAITE:       { label: 'Traite',       icon: 'fa-file-contract',   iconClass: 'mode--autre',    barClass: 'bar--autre' },
  };

  constructor(
    private statistiqueService: StatistiqueService,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnInit(): void { this.appliquerPeriode('MOIS_EN_COURS'); }

  appliquerPeriode(p: Periode): void {
    this.periodeSelectionnee = p;
    const today = new Date();
    if (p === 'MOIS_EN_COURS') {
      this.dateDebut = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
      this.dateFin   = this.formatDate(today);
    } else if (p === 'MOIS_PRECEDENT') {
      const debut = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const fin   = new Date(today.getFullYear(), today.getMonth(), 0);
      this.dateDebut = this.formatDate(debut);
      this.dateFin   = this.formatDate(fin);
    } else if (p === '7_JOURS') {
      const debut = new Date(today); debut.setDate(today.getDate() - 7);
      this.dateDebut = this.formatDate(debut);
      this.dateFin   = this.formatDate(today);
    } else if (p === 'TOUT') {
      this.dateDebut = '';
      this.dateFin   = '';
    }
    // CUSTOM : l'utilisateur remplit dateDebut/dateFin manuellement
    if (p !== 'CUSTOM') this.recharger();
  }

  recharger(): void {
    this.chargement = true;
    const role = (this.authService.obtenirUtilisateurCourant()?.role || '').toString().toUpperCase();
    const isGlobal = role === 'COMPTABLE_GENERAL' || role === 'ADMIN' || role === 'MANAGER';

    const params = this.buildParams();
    const source = isGlobal
      ? this.statistiqueService.obtenirDashboardComptabiliteGlobal(params)
      : this.statistiqueService.obtenirDashboardComptabiliteZone(params);

    source.subscribe({
      next: (d) => {
        this.data = d;
        this.chargement = false;
        this.derniereMaj = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      },
      error: () => { this.data = null; this.chargement = false; }
    });
  }

  private buildParams(): string {
    const p: string[] = [];
    if (this.dateDebut) p.push(`debut=${this.dateDebut}`);
    if (this.dateFin)   p.push(`fin=${this.dateFin}`);
    return p.length ? '?' + p.join('&') : '';
  }

  private formatDate(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  validerEncaissement(commandeId: number, valide: boolean): void {
    if (!commandeId) return;
    this.statistiqueService.validerEncaissement(commandeId, valide).subscribe({
      next: () => {
        this.toast.succes(valide ? 'Encaissement validé ✓' : 'Encaissement rejeté');
        this.recharger();
      },
      error: () => this.toast.erreur('Impossible de mettre à jour la validation')
    });
  }

  entries(obj: any): { key: string; value: any }[] {
    if (!obj) return [];
    return Object.keys(obj).map((k) => ({ key: k, value: obj[k] }));
  }

  // ── Listes filtrées ────────────────────────────────────────────

  get lignesDeclaration(): any[] {
    return this.filtrerLignes(this.data?.enAttenteValidation ?? []);
  }

  get lignesSansDeclaration(): any[] {
    return this.filtrerLignes(this.data?.livreesSansEncaissement ?? []);
  }

  private filtrerLignes(lignes: any[]): any[] {
    const terme = this.rechercheCommande.trim().toLowerCase();
    if (!terme) return lignes;
    return lignes.filter(r =>
      (r?.numeroCommande || '').toString().toLowerCase().includes(terme) ||
      (r?.livreurNom || '').toLowerCase().includes(terme)
    );
  }

  // ── Calculs ─────────────────────────────────────────────────────

  ecart(r: any): number {
    return Math.round((r.montantEncaisse ?? 0) - (r.montantAttendu ?? 0));
  }

  tauxEncaissement(): number {
    if (!this.data?.totalAttenduLivrees) return 0;
    return Math.min(100, Math.round((this.data.totalEncaisseLivreurs / this.data.totalAttenduLivrees) * 100));
  }

  tauxValidation(): number {
    const total = (this.data?.encaissementsValides ?? 0) + (this.data?.encaissementsNonValides ?? 0);
    if (!total) return 0;
    return Math.round(((this.data.encaissementsValides ?? 0) / total) * 100);
  }

  private maxMode(): number {
    const vals = this.entries(this.data?.encaisseParMode).map(e => e.value as number);
    return vals.length ? Math.max(...vals) : 1;
  }

  modePct(val: number): number {
    const m = this.maxMode();
    return m ? Math.round((val / m) * 100) : 0;
  }

  // ── Mode helpers ─────────────────────────────────────────────────

  modeLabel(key: string): string     { return this.MODE_META[key]?.label    ?? key ?? '—'; }
  modeIcon(key: string): string      { return this.MODE_META[key]?.icon     ?? 'fa-money-bill'; }
  modeIconClass(key: string): string { return this.MODE_META[key]?.iconClass ?? 'mode--autre'; }
  modeBarClass(key: string): string  { return this.MODE_META[key]?.barClass  ?? 'bar--autre'; }

  // ── Formatage ────────────────────────────────────────────────────

  formaterFCFA(v: number): string {
    if (!v && v !== 0) return '—';
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.0','') + 'M F';
    if (v >= 1_000)     return (v / 1_000).toFixed(0) + 'K F';
    return v.toLocaleString('fr-FR') + ' F';
  }

  delaiJours(dateLivraison: string): string {
    if (!dateLivraison) return '—';
    const diff = Math.floor((Date.now() - new Date(dateLivraison).getTime()) / 86_400_000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return '1 jour';
    return `${diff} jours`;
  }

  get labelPeriode(): string {
    if (this.periodeSelectionnee === 'CUSTOM') {
      return `${this.dateDebut} → ${this.dateFin}`;
    }
    return this.PERIODES.find(p => p.val === this.periodeSelectionnee)?.label ?? '';
  }
}
